import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Download, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatError } from "@/lib/growthApi";
import { adminPortal } from "@/lib/portalApi";
import { AdminShell, Tag } from "@/pages/AdminLogin";
import { INPUT, BTN, fmtDate } from "@/pages/AdminClients";

const EVENT_LABEL = { unlock: "Unlocked portal", view: "Viewed PDF", download_pdf: "Downloaded PDF", download_pptx: "Downloaded PowerPoint" };
const EVENT_STYLE = { unlock: "border-white/20 text-slate-400", view: "border-electric/50 text-electric", download_pdf: "border-gold/50 text-gold", download_pptx: "border-gold/50 text-gold" };
const LABEL = "mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-slate-500";

function AccessCode({ client, onSaved }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { onSaved(await adminPortal.patch(client.id, { access_code: code })); setCode(""); toast.success("Access code updated"); }
    catch (ex) { toast.error(formatError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={save} className="border border-white/10 bg-navy-light/30 p-6" data-testid="admin-access-code-form">
      <p className="eyebrow mb-4">Access code</p>
      <p className="text-xs text-slate-500">Last changed {fmtDate(client.code_updated_at)}. Codes are stored hashed and cannot be viewed, only replaced.</p>
      <label htmlFor="ac-code" className={`${LABEL} mt-5`}>New access code</label>
      <input id="ac-code" data-testid="admin-new-code-input" minLength={6} required value={code} onChange={(e) => setCode(e.target.value)} className={INPUT} autoComplete="off" />
      <button type="submit" disabled={busy} className={`${BTN} mt-4 w-full`} data-testid="admin-save-code-button">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Replace code</button>
    </form>
  );
}

function FilePick({ label, kind, state, setState }) {
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setState({ name: file.name, progress: 0, upload_id: null });
    try { const id = await adminPortal.upload(file, kind, (p) => setState((s) => ({ ...s, progress: p }))); setState({ name: file.name, progress: 100, upload_id: id }); }
    catch (ex) { toast.error(formatError(ex)); setState(null); }
  };
  return (
    <div>
      <label className={LABEL} htmlFor={`file-${kind}`}>{label}</label>
      <input id={`file-${kind}`} type="file" accept={kind === "pdf" ? ".pdf" : ".pptx"} onChange={onFile} data-testid={`admin-file-${kind}-input`} className="block w-full text-xs text-slate-400 file:mr-4 file:border file:border-white/20 file:bg-transparent file:px-3 file:py-2 file:font-mono file:text-[10px] file:uppercase file:tracking-[0.2em] file:text-slate-300 hover:file:border-gold" />
      {state && <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500" data-testid={`admin-file-${kind}-status`}>{state.name} · {state.upload_id ? "ready" : `uploading ${state.progress}%`}</p>}
    </div>
  );
}

function NewReport({ client, onCreated }) {
  const [f, setF] = useState({ title: "", description: "", report_date: "" });
  const [pdf, setPdf] = useState(null);
  const [pptx, setPptx] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const uploading = (pdf && !pdf.upload_id) || (pptx && !pptx.upload_id);
  const submit = async (e) => {
    e.preventDefault();
    if (!pdf?.upload_id && !pptx?.upload_id) { toast.error("Attach a PDF or PowerPoint file"); return; }
    setBusy(true);
    try { const r = await adminPortal.createReport(client.id, { ...f, pdf_upload_id: pdf?.upload_id, pptx_upload_id: pptx?.upload_id }); onCreated(r); setF({ title: "", description: "", report_date: "" }); setPdf(null); setPptx(null); toast.success("Report published to the portal"); }
    catch (ex) { toast.error(formatError(ex)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="border border-white/10 bg-navy-light/30 p-6" data-testid="admin-new-report-form">
      <p className="eyebrow mb-5">Publish a report</p>
      <div className="space-y-4">
        <div><label htmlFor="rp-title" className={LABEL}>Title</label><input id="rp-title" data-testid="admin-report-title-input" required value={f.title} onChange={set("title")} className={INPUT} /></div>
        <div><label htmlFor="rp-date" className={LABEL}>Report date</label><input id="rp-date" data-testid="admin-report-date-input" value={f.report_date} onChange={set("report_date")} className={INPUT} placeholder="7 October 2026" /></div>
        <div><label htmlFor="rp-desc" className={LABEL}>Description</label><textarea id="rp-desc" data-testid="admin-report-description-input" rows={3} value={f.description} onChange={set("description")} className={INPUT} /></div>
        <FilePick label="PDF file" kind="pdf" state={pdf} setState={setPdf} />
        <FilePick label="PowerPoint file" kind="pptx" state={pptx} setState={setPptx} />
      </div>
      <button type="submit" disabled={busy || uploading} className={`${BTN} mt-6 w-full`} data-testid="admin-publish-report-button">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Publish report</button>
    </form>
  );
}

export default function AdminClient() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const load = useCallback(() => adminPortal.client(id).then(setC).catch((e) => toast.error(formatError(e))), [id]);
  useEffect(() => { load(); }, [load]);
  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from the portal?`)) return;
    try { await adminPortal.deleteReport(r.id); toast.success("Report removed"); load(); } catch (ex) { toast.error(formatError(ex)); }
  };
  return (
    <AdminShell title={c ? c.name : "Client portal"}>
      {!c ? <Loader2 className="animate-spin text-gold" /> : (
        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div className="space-y-10">
            <div className="flex flex-wrap items-center gap-4" data-testid="admin-client-meta">
              <Link to="/admin/clients" className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-gold" data-testid="admin-back-to-clients">← All clients</Link>
              <a href={`/clients/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold hover:text-gold-glow" data-testid="admin-open-portal-link">/clients/{c.slug} <ArrowUpRight size={12} /></a>
              <Tag className="border-white/20 text-slate-300">{c.report_count} reports</Tag>
              <Tag className="border-electric/40 text-electric">{c.view_count} views</Tag>
              <Tag className="border-gold/40 text-gold">{c.download_count} downloads</Tag>
            </div>
            <section data-testid="admin-reports-section">
              <p className="eyebrow mb-5">Published reports</p>
              {c.reports.length === 0 ? <p className="text-sm text-slate-500" data-testid="admin-reports-empty">Nothing published yet.</p> : (
                <ul className="divide-y divide-white/10 border-y border-white/10">
                  {c.reports.map((r) => (
                    <li key={r.id} className="flex flex-wrap items-start justify-between gap-4 py-4" data-testid={`admin-report-${r.id}`}>
                      <div>
                        <p className="font-semibold text-white">{r.title}</p>
                        <p className="mt-1 text-xs text-slate-500">{r.report_date}{r.report_date && " · "}Published {fmtDate(r.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-3">
                        {r.pdf_name && <a href={adminPortal.fileUrl(r.id, "pdf")} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 hover:text-gold" data-testid={`admin-report-pdf-${r.id}`}><Download size={12} /> PDF</a>}
                        {r.pptx_name && <a href={adminPortal.fileUrl(r.id, "pptx")} className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-300 hover:text-gold" data-testid={`admin-report-pptx-${r.id}`}><Download size={12} /> PPTX</a>}
                        <button type="button" onClick={() => remove(r)} className="text-slate-500 hover:text-red-400" aria-label="Remove report" data-testid={`admin-report-delete-${r.id}`}><Trash2 size={14} /></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <section data-testid="admin-activity-section">
              <p className="eyebrow mb-5">Client activity</p>
              {c.events.length === 0 ? <p className="text-sm text-slate-500" data-testid="admin-activity-empty">No portal activity recorded yet.</p> : (
                <ul className="divide-y divide-white/5 border-y border-white/10">
                  {c.events.map((e) => (
                    <li key={e.id} className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm" data-testid={`admin-event-${e.id}`}>
                      <div className="flex items-center gap-3"><Tag className={EVENT_STYLE[e.type] || "border-white/20 text-slate-400"}>{EVENT_LABEL[e.type] || e.type}</Tag><span className="text-slate-300">{e.report_title || ""}</span></div>
                      <span className="font-mono text-xs text-slate-500">{fmtDate(e.ts)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
          <div className="space-y-6">
            <NewReport client={c} onCreated={() => load()} />
            <AccessCode client={c} onSaved={(u) => setC({ ...c, ...u })} />
          </div>
        </div>
      )}
    </AdminShell>
  );
}
